import { Button } from 'components/ui/button';
import { KlaxWelcome } from 'components/blocks/klax-welcome';
import { SigninForm } from 'features/auth/components/signin-form';
import { LoginOption } from 'constant/sso';
import { GRANT_TYPES } from 'constant/auth';
import BlocksIcon from 'assets/images/blocks.svg';
import { useGetLoginOptions } from 'features/auth/hooks/use-auth';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type BlocksLoginPageProps = {
  buttonLabel: string;
  isLoading?: boolean;
  reason?: string | null;
  onLogin: () => void;
};

export function BlocksLoginPage({
  buttonLabel,
  isLoading = false,
  reason,
  onLogin,
}: BlocksLoginPageProps) {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const hasSsoCallback = Boolean(searchParams.get('code') && searchParams.get('state'));

  const { data: loginOption } = useGetLoginOptions() as { data: LoginOption };
  /* const loginOption: LoginOption = {
    allowedGrantTypes: [GRANT_TYPES.password],
    ssoInfo: [],
  }; */

  // Ensure grant types "password" and "social" are included in allowedGrantTypes
  if (loginOption) {
    const requiredGrantTypes = [GRANT_TYPES.password, GRANT_TYPES.social];
    loginOption.allowedGrantTypes = [...requiredGrantTypes];
  }

  const isRedirecting = hasSsoCallback || isLoading;

  if (isRedirecting) {
    return (
      <div className="mx-auto flex w-full min-w-0 max-w-[438px] flex-col items-center justify-center lg:max-w-[480px] xl:max-w-[438px]">
        <KlaxWelcome
          title={t('REDIRECTING_PLEASE_WAIT', 'Redirecting... Please wait')}
          className="w-full min-w-0"
        />
        {hasSsoCallback && (
          <div className="hidden">
            <SigninForm loginOption={loginOption} variant="klax" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-[438px] flex-col gap-5 sm:gap-8 md:px-2 lg:max-w-[480px] xl:max-w-[438px] xl:px-0">
      <KlaxWelcome className="w-full min-w-0" />

      {reason === 'session_expired' && (
        <div className="rounded-lg border border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/20">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            Your session has expired. Please log in again.
          </p>
        </div>
      )}

      <Button
        type="button"
        className="flex h-[50px] w-full items-center justify-center gap-2 rounded-full border border-[#d1d1d1] bg-white px-6 py-3 text-sm font-medium leading-[1.2] text-black shadow-none sm:h-[54px] sm:gap-[12px] sm:px-10 sm:py-3.5 sm:text-base hover:bg-[#f5f5f5] hover:text-black dark:!border-true-white dark:!bg-transparent dark:!text-true-white dark:hover:!bg-white/10 dark:hover:!text-true-white"
        onClick={onLogin}
        loading={isLoading}
      >
        {!isLoading && (
          <>
            <img src={BlocksIcon} alt="Blocks" className="h-5 w-5" />
            <span>{buttonLabel}</span>
          </>
        )}
      </Button>

      <SigninForm loginOption={loginOption} variant="klax" />
    </div>
  );
}
